import {AfterViewInit, Component, Input, OnDestroy, ViewChild} from '@angular/core';
import {Contract} from '../../../shared/interface/contract';
import {ReplaySubject} from 'rxjs';
import {delay, distinctUntilChanged, first, takeUntil, tap} from 'rxjs/operators';
import {NgForm} from '@angular/forms';
import {MatPaginator, PageEvent} from '@angular/material/paginator';
import {ContractService} from '../../../shared/services/contract.service';
import {CountedAndPaginated} from '../../../shared/interface/response/counted-and-paginated';
import {DocumentAddDialogComponent} from '../../documents/document-add-dialog/document-add-dialog.component';
import {
  FILE_TYPE_ANNEX,
  FILE_TYPE_ANNEX_COMMISSION_ATTACHMENT,
  FILE_TYPE_ANNEX_OFWCA_APPLICATION,
  FILE_TYPE_CONTRACT,
  FILE_TYPE_CONTRACT_TERMINATION, FILE_TYPE_OFWCA_APPLICATION, REPORT_TYPE_CONTRACT
} from '../../../shared/services/dict.service';
import {MatDialog} from '@angular/material/dialog';
import {ConfirmDialogComponent} from '../../../shared/components/confirm-dialog/confirm-dialog.component';
import {MatSnackBar} from '@angular/material/snack-bar';
import {UserPermissions} from '../../../shared/interface/user-permissions';
import {AuthService} from '../../../shared/services/auth.service';
import {ActivatedRoute, Router} from '@angular/router';
import {Uuid} from '../../../shared/interface/response/uuid';
import {JobResultDialogComponent} from '../../../shared/components/job-result-dialog/job-result-dialog.component';
import {HttpErrorResponse} from '@angular/common/http';
import {FilterService} from '../../../shared/services/filter.service';
import {select, Store} from '@ngrx/store';
import {StoreRootState} from '../../../shared/ngrx/reducers';
import {getCurrentRouteState} from '../../../shared/ngrx/selector';
import {RouterStateUrl} from '../../../shared/ngrx/route-serializer';
import {ReportCustomComponent} from "../../report-custom/report-custom.component";

@Component({
  selector: 'app-all-contract-list',
  templateUrl: './all-contract-list.component.html',
  styleUrls: ['./all-contract-list.component.scss']
})
export class AllContractListComponent implements AfterViewInit, OnDestroy {
  @ViewChild('filtersForm') filtersForm: NgForm;
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @Input() typeKey?: string[] = null;
  @Input() statusKey?: string[] = null;
  @Input() selfBillingWithActiveParent?: number = null;
  @Input() displayedColumns: string[] = ['number', 'type', 'concludeDate', 'owner', 'party',
    'status', 'activeTo', 'assetsRegional', 'company', 'actions'];

  initialFilters: any;
  contracts: Contract[] = [];
  totalData = 0;
  userPermissions: ReplaySubject<UserPermissions>;

  private destroyed$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  constructor(
    private contractService: ContractService,
    private dialog: MatDialog,
    private snack: MatSnackBar,
    private auth: AuthService,
    private activatedRoute: ActivatedRoute,
    private filterService: FilterService,
    private store: Store<StoreRootState>,
    private router: Router,
  ) {
    this.userPermissions = auth.getUserPermissions();
    this.contractService.getContractListSubject()
      .pipe(
        takeUntil(this.destroyed$),
        tap((e: CountedAndPaginated<Contract>) => {
          this.contracts = e.items;
          this.totalData = e.pagination.total;
        })
      ).subscribe();
    this.parseFilters();
  }

  parseFilters(filters?: any): void {
    this.initialFilters = this.filterService.parseFiltersFromQueryParams({
      recordId: 'string',
      type: 'int[]',
      gte$concludeDate: 'date',
      lte$concludeDate: 'date',
      company: 'int',
      status: 'int[]',
      contains$number: 'string',
      gte$activeFrom: 'date',
      lte$activeFrom: 'date',
      gte$activeTo: 'date',
      lte$activeTo: 'date',
      terminationTime: 'int',
      terminationType: 'int',
      period: 'int',
      owner: 'int',
      partner: 'string',
      ofwca: 'string',
      assetsRegional: 'string',
      verified: 'int',
      signedCurrentApplications: 'int',
      newContractTemplate: 'int',
      selfBillingWithActiveParent: 'int',
      automaticBilling: 'int',
      customTerms: 'int',
      selfBillingExpects: 'int',
    }, filters);
  }

  ngAfterViewInit(): void {
    this.activatedRoute.data
      .pipe(
        delay(0),
        first()
      )
      .subscribe((data: any) => {
        this.statusKey = this.statusKey ?? data.statusKey ?? null;
        this.typeKey = this.typeKey ?? data.typeKey ?? null;
        this.selfBillingWithActiveParent = this.selfBillingWithActiveParent ?? data.selfBillingWithActiveParent ?? null;
        this.displayedColumns = data.displayedColumns ?? this.displayedColumns;

        this.store
          .pipe(
            select(getCurrentRouteState),
            delay(0),
            distinctUntilChanged((prev: RouterStateUrl, curr: RouterStateUrl) => prev.url === curr.url),
            takeUntil(this.destroyed$),
          )
          .subscribe((state: RouterStateUrl) => {
            this.parseFilters(state.params);
            this.paginator.pageIndex = state.params.page;
            this.paginator.pageSize = state.params.pageSize;
            this.loadListData();
          })
        ;
      });

    this.paginator.page
      .pipe(
        takeUntil(this.destroyed$)
      ).subscribe((event: PageEvent) => {
      this.router.navigate([this.activatedRoute.snapshot.routeConfig.path, this.filterService.parseFiltersToQueryParams({
        page: event.pageIndex,
        pageSize: event.pageSize,
        ...this.filtersForm.value
      })]);
    });
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  buildFilters(): any {
    const filters = {...this.initialFilters};

    if (null !== this.statusKey) {
      filters.statusKey = this.statusKey;
    }
    if (null !== this.typeKey) {
      filters.typeKey = this.typeKey;
    }

    if (null !== this.selfBillingWithActiveParent) {
      filters.selfBillingWithActiveParent = this.selfBillingWithActiveParent;
    }
    return filters;
  }

  loadListData(): void {
    this.contractService.loadList(this.paginator.pageIndex, this.paginator.pageSize, this.buildFilters());
  }

  filter(): void {
    this.router.navigate([this.activatedRoute.snapshot.routeConfig.path, this.filterService.parseFiltersToQueryParams({
      page: 0,
      pageSize: this.paginator.pageSize,
      ...this.filtersForm.value,
    })]);
  }

  downloadReport(): void {
    this.contractService.downloadReport(this.buildFilters())
      .subscribe((response: Uuid) => {
        this.dialog.open(JobResultDialogComponent, {
          disableClose: true,
          width: '500px',
          data: {jobId: response.uuid, showProgress: false},
        });
      }, (error: HttpErrorResponse) => {
        this.snack.open('Przy generowaniu raportu wystąpił błąd', error.status.toString(), {duration: 2000});
      });
  }

  resetFilters(): void {
    this.filtersForm.resetForm();
    this.router.navigate([this.activatedRoute.snapshot.routeConfig.path, {
      page: 0,
      pageSize: this.paginator.pageSize,
    }]);
  }

  addDocument(contract: Contract): void {
    this.dialog.open(DocumentAddDialogComponent, {
      width: '55%',
      data: {
        contractObject: contract,
        types: [
          FILE_TYPE_CONTRACT_TERMINATION,
          FILE_TYPE_CONTRACT,
          FILE_TYPE_ANNEX,
          FILE_TYPE_OFWCA_APPLICATION,
          FILE_TYPE_ANNEX_OFWCA_APPLICATION,
          FILE_TYPE_ANNEX_COMMISSION_ATTACHMENT,
        ],
      },
    });
  }

  deleteContract(contract: Contract): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '500px',
      data: {text: 'Czy na pewno chcesz usunąć umowę?'},
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.contractService.deleteContract(contract.uuid)
          .subscribe({
            next: () => {
              this.snack.open('Pomyślnie usunięto umowę', '', {duration: 2000});
              this.loadListData();
            },
            error: (response: HttpErrorResponse) => {
              this.snack.open(response.error.error.text, response.status.toString(), {duration: 2000});
            }
          });
      }
    });
  }
  openReportDialog(): void {
    const dialogRef = this.dialog.open(ReportCustomComponent, {
      width: '70%',
      data: {filter: this.filtersForm,
        reportName: REPORT_TYPE_CONTRACT},
    });
  }
}
